import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BookOpen, ChevronRight, SearchX } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { useAuth } from '../contexts/AuthContext';
import { GUIDE_ITEM, navForRole, navItemFor } from '../utils/navigation';
import { defaultPathForRole, roleOf } from '../utils/roles';

interface Props {
  /** missing: no such page. blocked: a page that exists, but not for this role. */
  reason?: 'missing' | 'blocked';
}

// A link that goes nowhere, or to a page this role cannot open, ends here, with the pages the person can open.
// It reads the same map as the menu (utils/navigation.ts), so it never offers a page the menu would not.
export default function NotFoundPage({ reason = 'missing' }: Props) {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const role = roleOf(user);
  const blocked = reason === 'blocked';
  const home = navItemFor(defaultPathForRole(role));
  const groups = navForRole(role);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100/60">
      <PageHeader title={blocked ? 'Not open to your role' : 'Page not found'} icon={SearchX} plain />
      <div className="p-4 sm:p-6">
        <div className="mx-auto max-w-3xl">
          <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-gray-200/70">
            <h2 className="text-lg font-semibold tracking-tight text-gray-900">{blocked ? "That page isn't open to your role" : "We can't find that page"}</h2>
            <p className="mt-1.5 text-sm leading-relaxed text-gray-600">
              {blocked ? 'Ask an admin if you need it. These are the pages you can open.' : 'The link may be old or mistyped. These are the pages you can open.'}
            </p>
            <p className="mt-3 break-all rounded-lg bg-gray-50 px-3 py-2 font-mono text-xs text-gray-600" aria-label="The address you asked for">
              {pathname}
            </p>
            {home && (
              <Link
                to={home.path}
                className="mt-4 inline-flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 motion-reduce:transition-none"
              >
                Go to {home.label}
              </Link>
            )}
          </div>

          <nav aria-label="Pages you can open" className="mt-6">
            {groups.map(({ group, items }) => (
              <section key={group.id} aria-labelledby={`nf-group-${group.id}`} className="mb-5">
                <h3 id={`nf-group-${group.id}`} className="text-[0.6875rem] font-semibold uppercase tracking-wider text-gray-500">
                  {group.caption}
                </h3>
                <ul className="mt-2 grid gap-2 sm:grid-cols-2">
                  {items.map((item) => {
                    const Icon = item.icon;
                    return (
                      <li key={item.path}>
                        <Link
                          to={item.path}
                          className="group flex h-full items-start gap-3 rounded-xl bg-white p-3.5 shadow-sm ring-1 ring-gray-200/70 transition-shadow hover:shadow-md hover:ring-gray-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 motion-reduce:transition-none"
                        >
                          <Icon className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" aria-hidden />
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-medium text-gray-900">{item.label}</span>
                            <span className="mt-0.5 block text-xs leading-snug text-gray-600">{item.purpose}</span>
                          </span>
                          <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-gray-400 transition-transform group-hover:translate-x-0.5 motion-reduce:transform-none" aria-hidden />
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ))}
          </nav>

          <p className="text-sm text-gray-600">
            Not sure where something lives?{' '}
            <Link to={GUIDE_ITEM.path} className="inline-flex items-center gap-1 font-medium text-blue-700 underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
              <BookOpen className="h-3.5 w-3.5" aria-hidden />
              The Guide
            </Link>{' '}
            explains every page.
          </p>
        </div>
      </div>
    </div>
  );
}
